import {
  SCENARIO_DOMAINS,
  domainLabel,
  type ScenarioDomainKey,
} from 'lib/evaluation-case/scenario-domains';
import type {
  AreaTagFields,
  CaseDomainEntry,
  CaseFormValues,
  CaseResponse,
  ReviewReason,
  ReviewStatus,
  ScenarioChoice,
} from './types';

export const EVALUATION_TYPE_OPTIONS = [
  { value: 'acquisition', label: 'Acquisition' },
  { value: 'divestiture', label: 'Divestiture' },
  { value: 'mtm', label: 'MTM' },
];

export const DISCOUNT_TO_DATE_TYPE_OPTIONS = [
  { value: 'floating', label: 'Start Of The Month' },
  { value: 'fixed', label: 'Fixed' },
] as const;

/** Every domain unassigned. */
export const EMPTY_SCENARIOS = Object.fromEntries(
  SCENARIO_DOMAINS.map((domain) => [domain.key, null])
) as Record<ScenarioDomainKey, ScenarioChoice | null>;

/** A new case: nothing but the defaults. Saving needs only a name and an evaluation type. */
export const DEFAULT_VALUES: CaseFormValues = {
  name: '',
  evaluationType: '',
  scope: '',
  notes: '',
  discountToDateType: 'floating',
  discountToDate: null,
  scenarios: EMPTY_SCENARIOS,
};

/** Required-field messages, shown once a save is attempted. */
export const NAME_REQUIRED = 'Case name is required';
export const EVALUATION_TYPE_REQUIRED = 'Evaluation type is required';
export const DISCOUNT_DATE_REQUIRED = 'Pick a date for a fixed discount date';

/**
 * The messages a save must clear: name, evaluation type, and a date when the discount date is
 * fixed. Every domain may be unassigned on save; a case-level run is what needs all eight.
 *
 * @param {CaseFormValues} values - The form values.
 * @return {object} `{ name, evaluationType, discountToDate }`, each a message or undefined.
 */
export function validateCase(values: CaseFormValues): {
  name?: string;
  evaluationType?: string;
  discountToDate?: string;
} {
  return {
    name: values.name.trim() ? undefined : NAME_REQUIRED,
    evaluationType: values.evaluationType
      ? undefined
      : EVALUATION_TYPE_REQUIRED,
    discountToDate:
      values.discountToDateType === 'fixed' && !values.discountToDate
        ? DISCOUNT_DATE_REQUIRED
        : undefined,
  };
}

/**
 * The three area-tag fields of anything that carries them, for the mappers to spread.
 *
 * @param {AreaTagFields} source - A scenario row, entry, suggestion or candidate.
 * @return {AreaTagFields} Its area tags.
 */
export const areaTagFields = ({
  areaTags,
  areaTagsMoreCount,
  areaTagsAppliesToAll,
}: AreaTagFields): AreaTagFields => ({
  areaTags,
  areaTagsMoreCount,
  areaTagsAppliesToAll,
});

/**
 * A domain entry's scenario as a choice, or null when unassigned.
 *
 * @param {CaseDomainEntry} entry - A `scenarioDomains[]` entry.
 * @return {ScenarioChoice | null} The choice.
 */
function entryChoice(entry: CaseDomainEntry): ScenarioChoice | null {
  if (entry.scenarioId === null) return null;

  return {
    id: entry.scenarioId,
    name: entry.scenarioName ?? `#${entry.scenarioId}`,
    ...areaTagFields(entry),
  };
}

/**
 * A loaded case as form values.
 *
 * @param {CaseResponse} response - `GET /evaluation_cases/:id`.
 * @return {CaseFormValues} The form values.
 */
export function mapResponseToForm(response: CaseResponse): CaseFormValues {
  const scenarios = { ...EMPTY_SCENARIOS };
  response.scenarioDomains.forEach((entry) => {
    scenarios[entry.domain] = entryChoice(entry);
  });

  return {
    name: response.name ?? '',
    evaluationType: response.evaluationType ?? '',
    scope: response.scope ?? '',
    notes: response.notes ?? '',
    discountToDateType: response.discountToDateType ?? 'floating',
    discountToDate: response.discountToDate,
    scenarios,
  };
}

/**
 * The archived scenario each archived domain held when the case loaded.
 *
 * @param {CaseResponse | null} loaded - The loaded case, if any.
 * @return {object} `{ domain key => scenario id }`.
 */
export function archivedScenarioIds(
  loaded: CaseResponse | null
): Partial<Record<ScenarioDomainKey, number>> {
  const ids: Partial<Record<ScenarioDomainKey, number>> = {};
  loaded?.scenarioDomains.forEach((entry) => {
    if (
      loaded.archivedDomains.includes(entry.domain) &&
      entry.scenarioId !== null
    ) {
      ids[entry.domain] = entry.scenarioId;
    }
  });
  return ids;
}

/**
 * The fields of the create / update body, each from the form values: the case's own fields and
 * the eight scenario columns, flat. Never areas, references or geometry priority — the
 * case-level model has none.
 *
 * @param {CaseFormValues} values - The form values.
 * @return {object} The body's fields, by column.
 */
function payloadFields(
  values: CaseFormValues
): Record<string, string | number | null> {
  const columns = Object.fromEntries(
    SCENARIO_DOMAINS.map((domain) => [
      domain.column,
      values.scenarios[domain.key]?.id ?? null,
    ])
  );

  return {
    name: values.name.trim(),
    evaluation_type: values.evaluationType,
    // An empty scope saves as null, not an empty string.
    scope: values.scope || null,
    notes: values.notes,
    discount_to_date_type: values.discountToDateType,
    discount_to_date:
      values.discountToDateType === 'fixed' ? values.discountToDate : null,
    ...columns,
  };
}

/**
 * The create / update body. A create sends every field. An update (`baseline` given) sends only
 * the fields the user changed from `baseline`, the values the form started from, so a field left
 * alone never overwrites a change someone else saved meanwhile. The discount date and its type
 * go together.
 *
 * @param {CaseFormValues} values - The form values.
 * @param {CaseFormValues} [baseline] - The values the form started from, for an update.
 * @return {object} `{ evaluation_case: { ... } }`.
 */
export function buildPayload(
  values: CaseFormValues,
  baseline?: CaseFormValues
): {
  evaluation_case: Record<string, string | number | null>;
} {
  const fields = payloadFields(values);
  if (!baseline) return { evaluation_case: fields };

  const before = payloadFields(baseline);
  const changed = Object.keys(fields).filter(
    (key) => fields[key] !== before[key]
  );
  const discount = ['discount_to_date_type', 'discount_to_date'];
  const keys = changed.some((key) => discount.includes(key))
    ? [...new Set([...changed, ...discount])]
    : changed;

  return {
    evaluation_case: Object.fromEntries(keys.map((key) => [key, fields[key]])),
  };
}

/** What each review reason means to the user. */
export const REVIEW_REASON_LABELS: Record<ReviewReason, string> = {
  notReviewed: 'not reviewed since it was assigned',
  // Acknowledging never clears this one: the status flags an archived scenario whatever the
  // bookmark says, so only reassigning the domain does.
  archived: 'the scenario was archived; reassign it to clear',
  scenarioEdited: 'the scenario or its areas were edited',
  priorityReordered: "the scenario's geometry priority changed",
  geometryEdited: 'an area shape it uses was edited',
  newerVersionPromoted: 'a newer version of an area it uses was promoted',
};

/**
 * One line per domain that needs review: "Taxes: the scenario was archived".
 *
 * @param {ReviewStatus} status - The case's review status.
 * @return {string[]} The lines, in registry order.
 */
export function reviewLines(status: ReviewStatus): string[] {
  return status.domains
    .filter((domain) => domain.reasons.length > 0)
    .map((domain) => {
      const label = domainLabel(domain.domain);
      const reasons = domain.reasons.map(
        (reason) => REVIEW_REASON_LABELS[reason] ?? reason
      );
      return `${label}: ${reasons.join('; ')}`;
    });
}

/**
 * Whether acknowledging would clear nothing: every remaining reason is 'archived', which only
 * reassigning the domain clears.
 *
 * @param {ReviewStatus} status - The case's review status.
 * @return {boolean} true when every flagged domain's reasons are all 'archived'.
 */
export function onlyArchivedReasons(status: ReviewStatus): boolean {
  const reasons = status.domains.flatMap((domain) => domain.reasons);
  return reasons.length > 0 && reasons.every((reason) => reason === 'archived');
}

/**
 * The acknowledgement body for what the banner showed: its `asOf` as `seenAt`, the domains with
 * reasons, and each one's area-config ids as shown (none for a domain without area configs).
 *
 * @param {ReviewStatus} status - The review status the banner showed.
 * @return {object} `{ seenAt, domains, areaConfigIds }`.
 */
export function buildAcknowledgement(status: ReviewStatus): {
  seenAt: string;
  domains: ScenarioDomainKey[];
  areaConfigIds: Partial<Record<ScenarioDomainKey, number[]>>;
} {
  const flagged = status.domains.filter((domain) => domain.reasons.length > 0);
  const areaConfigIds: Partial<Record<ScenarioDomainKey, number[]>> = {};
  flagged.forEach((domain) => {
    if (domain.areaConfigIds)
      areaConfigIds[domain.domain] = domain.areaConfigIds;
  });

  return {
    seenAt: status.asOf,
    domains: flagged.map((domain) => domain.domain),
    areaConfigIds,
  };
}

/**
 * Whether a save refused as stale (409) can go again as it is: the fields this user changed from
 * `baseline` and the ones someone else changed since (`fresh`) don't overlap. The retry then sends
 * the same fields, so it still overwrites nothing of theirs.
 *
 * @param {CaseFormValues} values - The form values.
 * @param {CaseFormValues} baseline - The values the form started from.
 * @param {CaseFormValues} fresh - The case as it is now.
 * @return {boolean} True when the two sets of changes share no field.
 */
export function changesAreDisjoint(
  values: CaseFormValues,
  baseline: CaseFormValues,
  fresh: CaseFormValues
): boolean {
  const mine = Object.keys(buildPayload(values, baseline).evaluation_case);
  const theirs = Object.keys(buildPayload(fresh, baseline).evaluation_case);
  return !mine.some((field) => theirs.includes(field));
}
